import { DeleteOutlined } from '@ant-design/icons';
import { Col, FormInstance, Row } from 'antd';
import { valueType } from 'antd/lib/statistic/utils';
import { useEffect } from 'react';

import {
  Button,
  FormItem,
  FormItemInput,
  FormItemInputNumber,
  Input,
} from '../../../../../components/index';
import { DiscountMode } from '../../../../../typesFinancial';

type ItemDescountRowProps = {
  index: number;
  onRemove?: (index: number | number[]) => void;
  itemRowProps?: {
    form: FormInstance;
    grossValue: number;
  };
};

export const itemDescountRowColumns = [{ title: 'Desconto', colSpan: 24 }];

const PERCENTUAL_DECIMALS = 4;
const VALUE_DECIMALS = 2;

export function ItemDescountRow(props: ItemDescountRowProps) {
  const { index, onRemove, itemRowProps } = props;
  const form = itemRowProps?.form;
  const grossValue = itemRowProps?.grossValue || 0;

  const mode: DiscountMode =
    form?.getFieldValue(['discount', 'items', index, 'mode']) || 'PERCENTUAL';
  const isPercentual = mode === 'PERCENTUAL';

  function derive(
    nextMode: DiscountMode,
    derivedField: 'percentual' | 'value',
    derivedValue: number | null
  ) {
    form?.setFields([
      { name: ['discount', 'items', index, 'mode'], value: nextMode },
      { name: ['discount', 'items', index, derivedField], value: derivedValue },
    ]);
  }

  function derivedValueOf(percentual: number) {
    return Number(((grossValue * percentual) / 100).toFixed(VALUE_DECIMALS));
  }

  function derivedPercentualOf(value: number) {
    return Number(((value * 100) / grossValue).toFixed(PERCENTUAL_DECIMALS));
  }

  useEffect(() => {
    if (!grossValue) {
      return;
    }

    const item = (form?.getFieldValue(['discount', 'items']) || [])[index];

    if (!item) {
      return;
    }

    if (isPercentual && item.percentual != null) {
      derive('PERCENTUAL', 'value', derivedValueOf(Number(item.percentual)));
    }

    if (!isPercentual && item.value != null) {
      derive('VALUE', 'percentual', derivedPercentualOf(Number(item.value)));
    }
  }, [grossValue]);

  function onPercentualChange(typed: valueType) {
    const percentual = Number(typed);
    const isDerivable = grossValue > 0 && !Number.isNaN(percentual);

    derive(
      'PERCENTUAL',
      'value',
      isDerivable ? derivedValueOf(percentual) : null
    );
  }

  function onValueChange(typed: valueType) {
    const value = Number(typed);
    const isDerivable = grossValue > 0 && !Number.isNaN(value);

    derive(
      'VALUE',
      'percentual',
      isDerivable ? derivedPercentualOf(value) : null
    );
  }

  return (
    <Row
      gutter={16}
      style={{
        background: '#fff',
        margin: '8px 16px 8px 16px',
        padding: '5px',
      }}
    >
      <FormItemInput
        colSpan={9}
        label="Nome"
        name={[index, 'name']}
        required
        maxLength={100}
      />
      <FormItemInputNumber
        colSpan={6}
        label="Percentual"
        name={[index, 'percentual']}
        addonAfter="%"
        style={{ marginBottom: '0px', opacity: isPercentual ? 1 : 0.6 }}
        required={isPercentual}
        maxLength={6}
        max={100}
        onChange={onPercentualChange}
        help={isPercentual ? undefined : 'Calculado'}
      />
      <FormItemInputNumber
        colSpan={7}
        label="Valor"
        name={[index, 'value']}
        addonBefore="R$"
        style={{ marginBottom: '0px', opacity: isPercentual ? 0.6 : 1 }}
        required={!isPercentual}
        onChange={onValueChange}
        help={isPercentual ? 'Calculado' : undefined}
      />
      <FormItem name={[index, 'mode']} hidden>
        <Input />
      </FormItem>
      <Col span={2}>
        <Button
          type="default"
          danger
          icon={<DeleteOutlined />}
          onClick={() => onRemove && onRemove(index)}
        />
      </Col>
    </Row>
  );
}
